import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TodoItem, Priority, Visibility } from '../types/todo';
import { useTodoContext } from '../contexts/TodoContext';
import { useToastContext } from '../contexts/ToastContext';
import { canAddChildToNode } from '../utils/tree';
import Modal from './Modal';

interface ContextMenuProps {
  id: string;
  item: TodoItem;
  position: { x: number; y: number };
  onClose: () => void;
  onAddChild?: (parentIds: string[]) => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ id, item, position, onClose, onAddChild }) => {
  const { tree, toggleBlocked, toggleArchived, setPriority, removeItem, addChild, updateDescription, setVisibility } = useTodoContext();
  const { showError } = useToastContext();
  const [showAddChild, setShowAddChild] = useState(false);
  const [childName, setChildName] = useState('');
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  const [showEditDescription, setShowEditDescription] = useState(false);
  const [editDescription, setEditDescription] = useState(item.description);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // While the delete confirmation modal is open, let the Modal manage its
      // own dismissal. The Modal is rendered in a separate portal (not inside
      // menuRef), so without this guard a mousedown on the modal's Delete
      // button would close the context menu — unmounting the modal before its
      // click handler runs, and silently cancelling the delete.
      if (showDeleteConfirm) return;
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      // Defer to the modal's own Escape handling while it is open.
      if (showDeleteConfirm) return;
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, showDeleteConfirm]);

  useEffect(() => {
    if (showAddChild && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showAddChild]);

  useEffect(() => {
    if (showEditDescription && descriptionRef.current) {
      descriptionRef.current.focus();
      descriptionRef.current.select();
    }
  }, [showEditDescription]);

  const handleAddChild = () => {
    // Check if parent has a time estimate before showing add child form
    if (!canAddChildToNode(tree, id)) {
      showError('Cannot add child to a task with a time estimate. Remove the estimate first.');
      return;
    }
    setShowAddChild(true);
  };

  const handleChildNameSubmit = () => {
    if (childName.trim()) {
      addChild(id, childName.trim(), (parentIds) => {
        onAddChild?.(parentIds);
      }, (message) => {
        showError(message);
      });
      onClose();
    }
  };

  const handleChildNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleChildNameSubmit();
    } else if (e.key === 'Escape') {
      setShowAddChild(false);
      setChildName('');
    }
  };

  const handleToggleBlocked = () => {
    toggleBlocked(id);
    onClose();
  };

  const handleToggleArchived = () => {
    toggleArchived(id);
    onClose();
  };

  const handleSetPriority = (priority: Priority) => {
    setPriority(id, priority);
    onClose();
  };

  const handleRemoveItem = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    removeItem(id);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleEditDescription = () => {
    setShowEditDescription(true);
    setEditDescription(item.description);
  };

  const handleDescriptionSubmit = () => {
    if (editDescription !== item.description) {
      updateDescription(id, editDescription);
    }
    setShowEditDescription(false);
    onClose();
  };

  const handleDescriptionKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleDescriptionSubmit();
    } else if (e.key === 'Escape') {
      setShowEditDescription(false);
      setEditDescription(item.description);
    }
  };

  const handleSetVisibility = (visibility: Visibility) => {
    setVisibility(id, visibility);
    onClose();
  };

  const getMenuStyle = (): React.CSSProperties => {
    const menuWidth = 200;
    const hasChildren = item.children.length > 0;
    const baseHeight = hasChildren ? 260 : 180; // Add space for children controls
    const menuHeight = showPriorityMenu ? 360 : showVisibilityMenu ? 320 : showAddChild ? 300 : showEditDescription ? 320 : baseHeight;

    let left = position.x;
    let top = position.y;

    // Adjust if menu would go off screen
    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 10;
    }
    if (top + menuHeight > window.innerHeight) {
      top = window.innerHeight - menuHeight - 10;
    }

    return {
      left: `${left}px`,
      top: `${top}px`,
    };
  };

  const priorities: Priority[] = ['low', 'medium', 'high', 'critical'];
  const visibilityOptions: Visibility[] = ['show-active', 'show-all', 'hide-all'];

  const getVisibilityLabel = (visibility: Visibility): string => {
    switch (visibility) {
      case 'show-active': return 'Show Active Only';
      case 'show-all': return 'Show All Children';
      case 'hide-all': return 'Hide All Children';
      default: return visibility;
    }
  };

  const getVisibilityIcon = (visibility: Visibility): string => {
    switch (visibility) {
      case 'show-active': return '🔍';
      case 'show-all': return '👁️';
      case 'hide-all': return '📁';
      default: return '🔍';
    }
  };

  const contextMenuElement = (
    <div
      ref={menuRef}
      className="context-menu"
      style={getMenuStyle()}
    >
      <div className="context-menu-header">
        <span className="context-menu-title">{item.name}</span>
      </div>

      {showAddChild ? (
        <div className="context-menu-add-child">
          <input
            ref={inputRef}
            type="text"
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            onKeyDown={handleChildNameKeyPress}
            onBlur={() => {
              if (!childName.trim()) {
                setShowAddChild(false);
              }
            }}
            placeholder="Enter child task name..."
            className="add-child-input"
          />
          <div className="add-child-buttons">
            <button onClick={handleChildNameSubmit} disabled={!childName.trim()}>
              Add
            </button>
            <button onClick={() => {
              setShowAddChild(false);
              setChildName('');
            }}>
              Cancel
            </button>
          </div>
        </div>
      ) : showEditDescription ? (
        <div className="context-menu-edit-description">
          <textarea
            ref={descriptionRef}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            onKeyDown={handleDescriptionKeyPress}
            onBlur={() => {
              if (editDescription === item.description) {
                setShowEditDescription(false);
              }
            }}
            placeholder="Enter description... (Ctrl+Enter to save, Esc to cancel)"
            className="edit-description-textarea"
          />
          <div className="edit-description-buttons">
            <button onClick={handleDescriptionSubmit}>
              Save
            </button>
            <button onClick={() => {
              setShowEditDescription(false);
              setEditDescription(item.description);
            }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <button className="context-menu-item" onClick={handleAddChild}>
            <span className="menu-icon">➕</span>
            Add Child Task
          </button>

          <button className="context-menu-item" onClick={handleEditDescription}>
            <span className="menu-icon">📝</span>
            Edit Description
          </button>

          <div className="context-menu-divider"></div>

          <button
            className="context-menu-item"
            onClick={() => setShowPriorityMenu(!showPriorityMenu)}
          >
            <span className="menu-icon">🎯</span>
            Priority: {item.priority}
            <span className="menu-arrow">{showPriorityMenu ? '▼' : '▶'}</span>
          </button>

          {showPriorityMenu && (
            <div className="priority-submenu">
              {priorities.map(priority => (
                <button
                  key={priority}
                  className={`priority-item ${item.priority === priority ? 'active' : ''}`}
                  onClick={() => handleSetPriority(priority)}
                >
                  {priority}
                </button>
              ))}
            </div>
          )}

          <button className="context-menu-item" onClick={handleToggleBlocked}>
            <span className="menu-icon">{item.blocked ? '✅' : '🚫'}</span>
            {item.blocked ? 'Unblock' : 'Block'} Task
          </button>

          <button className="context-menu-item" onClick={handleToggleArchived}>
            <span className="menu-icon">{item.archived ? '📂' : '📦'}</span>
            {item.archived ? 'Unarchive' : 'Archive'} Task
          </button>

          {item.children.length > 0 && (
            <>
              <div className="context-menu-divider"></div>

              <button
                className="context-menu-item"
                onClick={() => setShowVisibilityMenu(!showVisibilityMenu)}
              >
                <span className="menu-icon">{getVisibilityIcon(item.visibility)}</span>
                Children: {getVisibilityLabel(item.visibility)}
                <span className="menu-arrow">{showVisibilityMenu ? '▼' : '▶'}</span>
              </button>

              {showVisibilityMenu && (
                <div className="priority-submenu">
                  {visibilityOptions.map(visibility => (
                    <button
                      key={visibility}
                      className={`priority-item ${item.visibility === visibility ? 'active' : ''}`}
                      onClick={() => handleSetVisibility(visibility)}
                    >
                      <span className="menu-icon">{getVisibilityIcon(visibility)}</span>
                      {getVisibilityLabel(visibility)}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="context-menu-divider"></div>

          <button className="context-menu-item danger" onClick={handleRemoveItem}>
            <span className="menu-icon">🗑️</span>
            Delete Task
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      {createPortal(contextMenuElement, document.body)}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Task"
        message={`Are you sure you want to delete "${item.name}" and all its children?`}
        variant="confirm"
        confirmText="Delete"
        isDestructive
      />
    </>
  );
};

export default ContextMenu;